import { Download } from 'lucide-react'
import { useMemo, useState } from 'react'
import ExcelJS from 'exceljs'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { notify } from '@/lib/notify'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse } from '@/api/model'
import { useHealthInsuranceConfigs } from '../hooks/useHealthInsuranceConfigs.ts'
import { unwrap } from '@/utils/unwrap.ts'

interface Props {
  serviceModuleId: number
}

export function HealthInsuranceExportButton({ serviceModuleId }: Props) {
  const { t } = useTranslation('service-modules-insurance')
  const [isPending, setIsPending] = useState(false)

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(serviceModuleId)
  let employees = useMemo(
    () => unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const { healthConfigsByEmployeeId } = useHealthInsuranceConfigs(serviceModuleId)

  async function handleExport() {
    setIsPending(true)
    try {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Versicherung')

      const { data: refetchedData } = await employeesQuery.refetch();
      employees = unwrap<EmployeeUserResponse[]>(refetchedData) ?? [];

      worksheet.columns = [
        { header: 'Vorname', key: 'firstName', width: 20 },
        { header: 'Nachname', key: 'lastName', width: 20 },
        { header: 'Versicherungsnummer', key: 'insuranceNumber', width: 30 },
      ]

      for (const employee of employees) {
        const config = healthConfigsByEmployeeId.get(employee.id ?? '')
        worksheet.addRow({
          firstName: employee.firstName ?? '',
          lastName: employee.lastName ?? '',
          insuranceNumber: config?.healthInsuranceNumber ?? '',
        })
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'bKv-versicherungsnummern.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      notify.error(t('insurance.export.error'))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleExport} disabled={isPending}>
            <Download className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('insurance.exportButton')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
